import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { foyerApi } from "../../http/foyer";
import {
  STOREFRONT_ACCOUNT_KINDS,
  type StorefrontAccount,
  type StorefrontMember,
} from "@shared/storefront-customer";

/**
 * The supplier's own customers (`WL-3`).
 *
 * ## What this page is for
 *
 * A studio selling to hundreds of schools needs three answers on one screen:
 * who are my customers, who inside them can watch, and **what do they have
 * access to right now**. The third is the one nobody can look up anywhere else,
 * and it is derived from live grants rather than stored.
 *
 * ## ⚠️ The invitation token is shown ONCE
 *
 * It is a bearer credential — whoever holds it becomes that member. The server
 * stores only a hash, so this dialog is the only moment it exists in readable
 * form. The copy says so plainly rather than leaving somebody to discover it
 * when they close the dialog and go looking.
 *
 * ## Copy
 *
 * The audience is a commercial partner, not an operator. Empty states say what
 * happens next, never "no data".
 */
export default function SupplierCustomersPage() {
  const [accounts, setAccounts] = useState<StorefrontAccount[] | null>(null);
  const [members, setMembers] = useState<StorefrontMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<string, number>>({});
  const [newAccount, setNewAccount] = useState({ name: "", kind: "school" });
  const [invite, setInvite] = useState({ email: "", accountId: "" });
  const [issued, setIssued] = useState<{ email: string; token: string } | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, people] = await Promise.all([
        foyerApi.storefrontAccounts(),
        foyerApi.storefrontMembers(),
      ]);
      setAccounts(list);
      setMembers(people);
      // One access read per organisation. A studio has hundreds of customers,
      // not hundreds of thousands, and the answer is the reason to be here.
      const counts: Record<string, number> = {};
      for (const account of list) {
        const held = await foyerApi.storefrontAccountAccess(account.id);
        counts[account.id] = held.assetIds.length;
      }
      setAccess(counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addAccount() {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.createStorefrontAccount(newAccount);
      setNewAccount({ name: "", kind: "school" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite() {
    setBusy(true);
    setError(null);
    try {
      const result = await foyerApi.inviteStorefrontMember({
        email: invite.email,
        // An empty selection means a PRIVATE customer — one person, one
        // licence, no organisation. That is a normal customer, not an edge.
        accountId: invite.accountId || null,
      });
      setIssued({ email: result.member.email, token: result.token });
      setInvite({ email: "", accountId: "" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!accounts) return <CircularProgress size={24} />;

  const privateCustomers = members.filter((m) => !m.accountId);

  return (
    <Stack spacing={4}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Typography variant="h5" gutterBottom>
          Your customers
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          The organisations you sell to, and what each one can watch right now.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <TextField
            size="small"
            label="Organisation"
            value={newAccount.name}
            onChange={(e) =>
              setNewAccount({ ...newAccount, name: e.target.value })
            }
          />
          <TextField
            size="small"
            select
            label="Kind"
            value={newAccount.kind}
            onChange={(e) =>
              setNewAccount({ ...newAccount, kind: e.target.value })
            }
            sx={{ minWidth: 140 }}
          >
            {STOREFRONT_ACCOUNT_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {kind}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={busy || !newAccount.name.trim()}
            onClick={addAccount}
          >
            Add customer
          </Button>
        </Stack>

        {accounts.length === 0 ? (
          <Alert severity="info">
            No customers yet. Add the first organisation above, then invite the
            people who will watch.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>People</TableCell>
                <TableCell>Titles held now</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    {account.name}
                    {!account.enabled && (
                      <Chip size="small" label="paused" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{account.kind}</TableCell>
                  <TableCell>
                    {members.filter((m) => m.accountId === account.id).length}
                  </TableCell>
                  <TableCell>{access[account.id] ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          Invite somebody
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Leave the organisation empty for a private customer — one person, one
          licence. That is a normal customer, not a special case.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <TextField
            size="small"
            label="Email"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
          />
          <TextField
            size="small"
            select
            label="Organisation"
            value={invite.accountId}
            onChange={(e) =>
              setInvite({ ...invite, accountId: e.target.value })
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">No organisation</MenuItem>
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={busy || !invite.email.includes("@")}
            onClick={sendInvite}
          >
            Invite
          </Button>
        </Stack>

        {privateCustomers.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {privateCustomers.length} private customer
            {privateCustomers.length === 1 ? "" : "s"} with no organisation.
          </Typography>
        )}
      </Box>

      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)}>
        <DialogTitle>Send this link to {issued?.email}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time this token is readable. We store a hash of it,
            not the token — if it is lost, invite them again.
          </Alert>
          <TextField
            fullWidth
            multiline
            value={issued?.token ?? ""}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssued(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
